import React from "react";
import { Text, View } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";

import styles from "../../styles";
import { chunkTableRows, formatDisplayValue, roundedCornerStyle } from "../../helpers";

/** Approx. rows that fit on one A4 content area with header/footer. */
const ROWS_PER_PAGE = 18;
const TABLE_MIN_PRESENCE = 120;

const resolveColumnWidth = (column, columnCount) => {
    if (column?.width) {
        return column.width;
    }

    if (!columnCount) {
        return "100%";
    }

    return `${(100 / columnCount).toFixed(4)}%`;
};

const resolveCellValue = (column, record, index) => {
    if (typeof column?.render === "function") {
        return column.render(record, index);
    }

    if (!record || column?.key == null) {
        return null;
    }

    return record[column.key];
};

const EntityRecordsTable = ({
    t,
    columns = [],
    records = [],
    breakBefore = false,
    emptyMessage = "No data available",
    getRowKey,
    leading = null,
}) => {
    const safeColumns = Array.isArray(columns) ? columns.filter(Boolean) : [];
    const safeRecords = Array.isArray(records) ? records : [];
    const columnCount = safeColumns.length;

    if (!columnCount) {
        return null;
    }

    const headerCellStyle = {
        ...styles.tableColHeader,
        ...styles.wordTableHeaderCell,
    };

    const headerTextStyle = {
        ...styles.tableCell,
        ...styles.fontBold,
        ...styles.wordTableHeaderText,
    };

    const renderHeaderCell = (column, index) => {
        const width = resolveColumnWidth(column, columnCount);

        return (
            <View
                key={column.key || `header-${index}`}
                style={{
                    ...headerCellStyle,
                    width,
                    ...roundedCornerStyle({
                        isFirstRow: true,
                        isFirstCol: index === 0,
                        isLastCol: index === columnCount - 1,
                    }),
                }}
            >
                <Text style={headerTextStyle}>{t(column.label || column.key || "")}</Text>
            </View>
        );
    };

    const renderBodyCell = (key, value, width, edges = {}) => (
        <View
            key={key}
            style={{
                ...styles.tableCol,
                ...styles.entityRecordsCell,
                width,
                ...roundedCornerStyle(edges),
            }}
        >
            <Text style={styles.tableCell}>{formatDisplayValue(value)}</Text>
        </View>
    );

    const headerRow = (
        <View style={styles.tableRow}>
            {safeColumns.map(renderHeaderCell)}
        </View>
    );

    const chunks = safeRecords.length
        ? chunkTableRows(safeRecords, ROWS_PER_PAGE)
        : [[]];

    const tables = chunks.map((chunk, chunkIndex) => {
        const isFirstChunk = chunkIndex === 0;
        const globalOffset = chunkIndex * ROWS_PER_PAGE;

        const bodyRows = chunk.length
            ? chunk.map((record, index) => {
                const absoluteIndex = globalOffset + index;
                const isLast = index === chunk.length - 1;
                const rowStyle = {
                    ...styles.tableRow,
                    ...(absoluteIndex % 2 === 1 ? styles.tableRowAlt : {}),
                    ...(isLast ? styles.tableLastRow : {}),
                };

                const rowKey =
                    typeof getRowKey === "function"
                        ? getRowKey(record, absoluteIndex)
                        : record?.id ?? `entity-row-${absoluteIndex}`;

                return (
                    <View style={rowStyle} key={rowKey}>
                        {safeColumns.map((column, columnIndex) => {
                            const width = resolveColumnWidth(column, columnCount);
                            const value = resolveCellValue(column, record, absoluteIndex);

                            return renderBodyCell(
                                column.key || `cell-${columnIndex}`,
                                value,
                                width,
                                {
                                    isLastRow: isLast,
                                    isFirstCol: columnIndex === 0,
                                    isLastCol: columnIndex === safeColumns.length - 1,
                                }
                            );
                        })}
                    </View>
                );
            })
            : (
                <View style={{ ...styles.tableRow, ...styles.tableLastRow }}>
                    {safeColumns.map((column, columnIndex) => {
                        const width = resolveColumnWidth(column, columnCount);
                        const value = columnIndex === 0 ? t(emptyMessage) : "-";

                        return renderBodyCell(
                            column.key || `empty-${columnIndex}`,
                            value,
                            width,
                            {
                                isLastRow: true,
                                isFirstCol: columnIndex === 0,
                                isLastCol: columnIndex === safeColumns.length - 1,
                            }
                        );
                    })}
                </View>
            );

        return (
            <View
                key={`entity-table-chunk-${chunkIndex}`}
                wrap={false}
                minPresenceAhead={isFirstChunk ? TABLE_MIN_PRESENCE : 80}
                {...(breakBefore && isFirstChunk ? { break: true } : {})}
            >
                {isFirstChunk ? leading : null}
                <View style={{ ...styles.table, ...styles.entityRecordsTable }}>
                    {headerRow}
                    {bodyRows}
                </View>
            </View>
        );
    });

    return <View>{tables}</View>;
};

export default withNamespaces()(EntityRecordsTable);
